import { ShieldAlert, ArrowLeft, Zap, UserCheck, AlertTriangle, CreditCard, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/logo.png";
import SEO from "@/components/SEO";

const Refunds = () => {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <SEO
        title="Refund Policy — GreyRock AI®"
        description="GreyRock AI operates on a one-time credit model with a strict no-refund policy. Read how purchases, credits and edge-case exceptions are handled."
        path="/refunds"
      />
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(180deg, hsl(0 0% 10%) 0%, hsl(0 0% 5%) 100%)",
        }}
      />

      <div className="relative z-10 max-w-2xl mx-auto px-6 py-12 space-y-8">
        <div className="flex items-center gap-3 mb-8">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-cyan transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <img src={logo} alt="GreyRock AI" className="w-10 h-10 object-contain" />
          <span className="font-bold text-lg">
            <span className="text-foreground">Grey</span>
            <span className="text-cyan">Rock</span>
            <span className="text-foreground"> AI</span>
            <sup className="text-[10px] text-muted-foreground">&reg;</sup>
          </span>
        </div>

        <h1 className="text-3xl font-bold">
          <span className="text-foreground">Refund </span>
          <span className="text-cyan">Policy</span>
        </h1>

        <p className="text-muted-foreground leading-relaxed">
          This policy governs the use of GreyRock AI&reg;, a forensic text analysis platform owned and operated by Should I Studios LLC.
        </p>

        <p className="text-muted-foreground text-sm">
          Last updated: October 26, 2026
        </p>

        {/* 1. Digital Nature of Service */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-cyan" />
            <h2 className="text-xl font-semibold text-foreground">1. Digital Nature of Service</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            GreyRock AI&reg; provides digital, intangible goods (AI-generated response logic and psychological analysis tools). Because our service is delivered instantly upon purchase, we operate a strict no-refund policy.
          </p>
        </section>

        {/* 2. Consent to Immediate Access */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <UserCheck className="w-5 h-5 text-cyan" />
            <h2 className="text-xl font-semibold text-foreground">2. Consent to Immediate Access</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            By purchasing a credit pack from GreyRock AI&reg;, you explicitly request and consent to immediate access to the digital credits and tools those credits unlock. You acknowledge that by purchasing and accessing the AI tools, you waive your statutory right to a 14-day &ldquo;cooling-off&rdquo; period or withdrawal under applicable consumer law (including EU and UK regulations).
          </p>
        </section>

        {/* 3. Faulty Items & Technical Issues */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-cyan" />
            <h2 className="text-xl font-semibold text-foreground">3. Faulty Items &amp; Technical Issues</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            We are committed to providing a high-quality experience. If the service is &ldquo;faulty&rdquo; (e.g., a verified technical bug prevents you from using the tool for a significant period), you are entitled to a resolution.
          </p>
          <div className="rounded-xl border border-glass-border bg-charcoal-dark/50 p-4 space-y-3">
            <p className="text-muted-foreground leading-relaxed">
              <span className="text-foreground font-medium">Resolution Path:</span> We will first attempt to repair the service or provide a workaround.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              <span className="text-foreground font-medium">Refund Eligibility:</span> If the service remains unusable due to a verified fault on our end that we cannot fix within 7 business days, a partial or full refund may be issued at our discretion.
            </p>
          </div>
        </section>

        {/* 4. Cancellations */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-cyan" />
            <h2 className="text-xl font-semibold text-foreground">4. Purchases & Credits</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            GreyRock AI&reg; sells one-time credit packs — there is no subscription and no recurring billing. Credits are added to your account immediately after checkout and never expire. Because purchases are final once delivered, there is nothing to cancel.
          </p>
          <div className="rounded-xl border border-glass-border bg-charcoal-dark/50 p-4">
            <p className="text-muted-foreground leading-relaxed">
              <span className="text-foreground font-medium">How credits work:</span> Each analysis uses a fixed number of credits, shown before you confirm. You are only ever charged once per purchase, and you are never billed again automatically.
            </p>
          </div>
        </section>

        {/* 5. Unauthorized Charges */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-cyan" />
            <h2 className="text-xl font-semibold text-foreground">5. Unauthorized Charges</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            If you believe there has been an unauthorized charge, please contact us immediately. We investigate all fraudulent claims and will issue a refund if the charge is verified as unauthorized before the service was accessed.
          </p>
        </section>

        {/* 6. Contact Us */}
        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-cyan" />
            <h2 className="text-xl font-semibold text-foreground">6. Contact Us</h2>
          </div>
          <p className="text-muted-foreground leading-relaxed">
            For any questions or to report a technical fault:<br />
            Email:{" "}
            <a href="mailto:support@greyrockai.app" className="text-cyan hover:underline">support@greyrockai.app</a>
          </p>
        </section>

        {/* Back to Home Button */}
        <div className="pt-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-cyan/10 text-cyan font-medium hover:bg-cyan/20 transition-colors border border-cyan/20"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>

        <footer className="pt-8 border-t border-glass-border text-center space-y-3">
          <div className="flex flex-wrap items-center justify-center gap-4 text-xs">
            <a href="/about" className="text-muted-foreground hover:text-cyan transition-colors">About</a>
            <span className="text-muted-foreground/40">&bull;</span>
            <a href="/contact" className="text-muted-foreground hover:text-cyan transition-colors">Contact</a>
            <span className="text-muted-foreground/40">&bull;</span>
            <a href="/privacy" className="text-muted-foreground hover:text-cyan transition-colors">Privacy Policy</a>
            <span className="text-muted-foreground/40">&bull;</span>
            <a href="/terms" className="text-muted-foreground hover:text-cyan transition-colors">Terms of Service</a>
            <span className="text-muted-foreground/40">&bull;</span>
            <a href="/refunds" className="text-muted-foreground hover:text-cyan transition-colors">Refund Policy</a>
          </div>
          <p className="text-sm text-muted-foreground">&copy; 2026 GreyRock AI&reg;. All Rights Reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default Refunds;
